/**
 * Scrolls the nearest scrollable ancestor (overflow: auto/scroll) to center the element.
 * Avoids the browser bug where scrollIntoView() targets overflow: hidden containers.
 */
export function scrollElementToCenter(element: HTMLElement): void {
  // First try the explicit data-scroll-container attribute
  let scrollParent: HTMLElement | null = element.closest('[data-scroll-container]') as HTMLElement | null;

  // Fall back to DOM-walking overflow check
  if (!scrollParent) {
    scrollParent = element.parentElement;
    while (scrollParent) {
      const { overflowY } = getComputedStyle(scrollParent);
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      scrollParent = scrollParent.parentElement;
    }
  }

  if (!scrollParent) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  const containerRect = scrollParent.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const scrollTop =
    scrollParent.scrollTop +
    (elementRect.top - containerRect.top) -
    containerRect.height / 2 +
    elementRect.height / 2;

  scrollParent.scrollTo({ top: scrollTop, behavior: 'smooth' });
}

/**
 * Fields that live inside the Sign-Off sub-section of a BatchRecordCard.
 */
export const BATCH_SIGN_OFF_FIELDS = new Set([
  'productionStaffName',
  'productionStaffDate',
  'supervisorQaName',
  'supervisorQaDate'
]);

/**
 * Maps a batch record field name to its HTML element ID.
 * `productId` shares the same cell as `productName`.
 */
export function getBatchFieldElementId(recordId: string, fieldName: string): string {
  if (fieldName === 'productId') return `batch-${recordId}-field-productName`;
  return `batch-${recordId}-field-${fieldName}`;
}

/**
 * Maps an ITC entry field name to its HTML element ID.
 * `dateReceived` falls back to the row-level ID since it has no dedicated `<td>`.
 */
export function getItcFieldElementId(entryId: string, fieldName: string): string {
  if (fieldName === 'dateReceived') return `itc-entry-${entryId}`;
  return `itc-${entryId}-field-${fieldName}`;
}

/**
 * Finds an element by ID, scrolls it to center, and applies a temporary blue highlight.
 * Returns whether the element was found.
 */
export function scrollToFieldAndHighlight(
  elementId: string,
  options?: { delay?: number; highlightDuration?: number; highlightColor?: 'blue' | 'red' }
): boolean {
  const { delay = 400, highlightDuration = 3000, highlightColor = 'blue' } = options || {};

  const colors = highlightColor === 'red'
    ? { bg: 'rgba(244, 67, 54, 0.15)', border: '#F44336' }
    : { bg: 'rgba(33, 150, 243, 0.15)', border: '#2196F3' };

  const doScroll = () => {
    const el = document.getElementById(elementId);
    if (!el) return false;

    scrollElementToCenter(el);

    // Apply highlight
    const prevBackground = el.style.backgroundColor;
    const prevBoxShadow = el.style.boxShadow;
    const prevTransition = el.style.transition;

    el.style.transition = 'background-color 0.3s ease, box-shadow 0.3s ease';
    el.style.backgroundColor = colors.bg;
    el.style.boxShadow = `inset 0 0 0 2px ${colors.border}`;

    setTimeout(() => {
      el.style.backgroundColor = prevBackground;
      el.style.boxShadow = prevBoxShadow;
      setTimeout(() => {
        el.style.transition = prevTransition;
      }, 300);
    }, highlightDuration);

    return true;
  };

  if (delay > 0) {
    setTimeout(doScroll, delay);
    return true; // optimistic — element may not exist yet
  }
  return doScroll();
}
